import type { TOC } from '@ember/component/template-only';
import { PrismBadge } from '@mylighthouse/prism-ember';
import { eq, or } from 'ember-truth-helpers';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';

import styles from './page-template.module.css';

interface SettingsPageTemplateSignature {
  Args: {
    badge?: string;
    description?: string;
    extraInfo?: string;
    hasAccountSelector?: boolean;
    isFullHeight?: boolean;
    title?: string;
    /**
     * This is to be used when the content of the page should be contained within a specific width.
     * The default max width is 1840px, but when `type` is set to "container", the content will be contained within 800px.
     */
    type?: 'container';
  };
  Blocks: {
    breadcrumb?: [];
    default: [];
    headerAfter?: [];
    link?: [];
    title?: [];
  };
  Element: HTMLDivElement;
}

const SettingsPageTemplate = <template>
  <div
    class="{{styles.template}}
      {{if (eq @type 'container') styles.container}}
      {{if @isFullHeight 'overflow-y-auto'}}
      flex flex-1 flex-col"
    data-test-page-template
    ...attributes
  >
    {{#if (has-block "breadcrumb")}}
      <div class="mb-100">
        {{yield to="breadcrumb"}}
      </div>
    {{/if}}

    <header class="mb-600 flex justify-between">
      <div class="grid gap-200">
        <div class="flex items-center gap-200">
          {{#if (has-block "title")}}
            {{yield to="title"}}
          {{else}}
            <h2 data-test-title>{{@title}}</h2>
          {{/if}}

          {{#if @badge}}
            <PrismBadge @badgeType="info" @label={{@badge}} />
          {{/if}}

          {{#if @hasAccountSelector}}
            <HotelAndMarketDropdownsAccountSelectorWrapper />
          {{/if}}
        </div>

        {{#if (or @description (has-block "link"))}}
          <p
            class="{{styles.description}} paragraph-300--regular"
            data-test-description
          >
            {{#if @description}}
              {{@description}}
            {{/if}}
            {{yield to="link"}}
          </p>
        {{/if}}

        {{#if @extraInfo}}
          <p class="{{styles.extra-info}} paragraph-300--regular">
            {{@extraInfo}}
          </p>
        {{/if}}
      </div>

      {{yield to="headerAfter"}}
    </header>

    <div class={{if @isFullHeight "flex-1 overflow-y-auto"}}>
      {{yield}}
    </div>
  </div>
</template> satisfies TOC<SettingsPageTemplateSignature>;

export default SettingsPageTemplate;
